/**
 * Persisted local state — the localStorage shape AND the JSON backup format.
 *
 * This shape is deliberately Swift-`Codable`-friendly, because a backup file
 * exported from the web app must import straight into the future iOS app
 * (that's the whole sync story — there is no server):
 *
 * - Flat records keyed by plain strings, no nested Maps.
 * - Dates are ISO strings, never epoch numbers or Date objects.
 * - No `undefined` anywhere — every field is present, absent values are `null`.
 * - An explicit `v` version field with a tested {@link migrate} ladder.
 *
 * Note: filters are NOT here — the URL is the filter store (shareable
 * permalinks). Only progress and settings persist.
 */

import { z } from "zod";
import type { EntryId, ISODate } from "./catalog.ts";

/**
 * Current persisted-state version. Bump when the shape changes, add a rung to
 * {@link MIGRATIONS}, and extend the ladder tests.
 */
export const PERSIST_VERSION = 2;

/** One watched entry. Keys mirror the Letterboxd CSV columns they round-trip with. */
export interface WatchRecord {
  /** `"YYYY-MM-DD"` — the day it was watched (local date, no time). */
  watchedAt: ISODate;
  /** Rating on Letterboxd's 1–10 half-star scale; `null` = unrated. */
  rating10: number | null;
  rewatch: boolean;
  /** Where this record came from, so imports can be re-run additively. */
  source: "manual" | "letterboxd";
}

/**
 * When an entry was un-watched, as epoch milliseconds.
 *
 * A tombstone, kept deliberately instead of just dropping the record. Sync
 * unions watched entries, so absence cannot be told apart from "this device
 * never heard of it" — without a record of the deletion, un-ticking on one
 * device is silently undone by the next sync from a device that still has it.
 *
 * Kept in its own map rather than as a flag on WatchRecord so that everything
 * reading `watched` — the road, the solver, the counts, the Letterboxd export
 * — needs no knowledge of them. Only merge and the sweep look here.
 */
export type Tombstones = Record<EntryId, number>;

/** Persisted user settings (the `settings` zustand store). */
export interface PersistedSettings {
  /** Split the road into two legs at the Endgame Encore milestone. */
  encoreMode: boolean;
  /** Watch-time budget powering the solver and pace math. */
  hoursPerWeek: number;
  /** What drives the phase theme: scroll position, watch progress, or nothing. */
  themeSource: "scroll" | "progress" | "off";
  /** Let the solver substitute runtime.alternatives for full watches. */
  alternativesEnabled: boolean;
  /** Show the project's own rating on entry cards. */
  showRating: boolean;
}

/**
 * Everything we persist, and exactly what a backup file contains.
 * `watched` is keyed by {@link EntryId} — ~79 keys at most.
 */
export interface PersistedState {
  v: typeof PERSIST_VERSION;
  watched: Record<EntryId, WatchRecord>;
  /** Un-watches, so a deletion survives a merge. See {@link Tombstones}. */
  deleted: Tombstones;
  settings: PersistedSettings;
}

export const DEFAULT_SETTINGS: PersistedSettings = {
  encoreMode: false,
  hoursPerWeek: 6,
  themeSource: "scroll",
  alternativesEnabled: true,
  showRating: true,
};

export const EMPTY_STATE: PersistedState = {
  v: PERSIST_VERSION,
  watched: {},
  deleted: {},
  settings: DEFAULT_SETTINGS,
};

const isoDate = z.iso.date();

export const watchRecordSchema = z.object({
  watchedAt: isoDate,
  rating10: z.number().int().min(1).max(10).nullable(),
  rewatch: z.boolean(),
  source: z.enum(["manual", "letterboxd"]),
});

export const persistedSettingsSchema = z.object({
  encoreMode: z.boolean(),
  hoursPerWeek: z.number().positive().max(168),
  themeSource: z.enum(["scroll", "progress", "off"]),
  alternativesEnabled: z.boolean(),
  showRating: z.boolean(),
});

export const persistedStateSchema = z.object({
  v: z.literal(PERSIST_VERSION),
  watched: z.record(z.string(), watchRecordSchema),
  deleted: z.record(z.string(), z.number().int().nonnegative()),
  settings: persistedSettingsSchema,
});

/* ------------------------------------------------------------------------ *
 * Migration ladder
 * ------------------------------------------------------------------------ */

export class PersistMigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PersistMigrationError";
  }
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * v0 → v1. Version 0 is the pre-versioned dev-preview shape: no `v` field,
 * `watched` was a flat map of entry id → ISO watched-date string, and
 * `settings` (if present at all) only knew `hoursPerWeek`.
 */
function migrate0to1(state: UnknownRecord): UnknownRecord {
  const watched: Record<string, WatchRecord> = {};
  if (isRecord(state.watched)) {
    for (const [id, value] of Object.entries(state.watched)) {
      if (typeof value !== "string") {
        throw new PersistMigrationError(
          `v0 watched["${id}"] should be an ISO date string, got ${typeof value}`,
        );
      }
      watched[id] = { watchedAt: value, rating10: null, rewatch: false, source: "manual" };
    }
  }

  const oldSettings = isRecord(state.settings) ? state.settings : {};
  const settings: PersistedSettings = {
    ...DEFAULT_SETTINGS,
    ...(typeof oldSettings.hoursPerWeek === "number"
      ? { hoursPerWeek: oldSettings.hoursPerWeek }
      : {}),
  };

  return { v: 1, watched, settings };
}

/**
 * One rung per version: `MIGRATIONS[n]` transforms a version-`n` state into
 * version `n + 1`. Each rung only needs to know about its two adjacent
 * shapes; {@link migrate} walks them in order.
 */
/**
 * v1 → v2. Adds the tombstone map. Nothing to convert: a v1 state simply has
 * no record of any deletion, which is exactly what an empty map means.
 */
function migrate1to2(state: UnknownRecord): UnknownRecord {
  return { ...state, v: 2, deleted: {} };
}

const MIGRATIONS: Record<number, (state: UnknownRecord) => UnknownRecord> = {
  0: migrate0to1,
  1: migrate1to2,
};

/**
 * Migrate a persisted state from `fromVersion` up to {@link PERSIST_VERSION},
 * then validate the result. Throws {@link PersistMigrationError} on states
 * from the future or with no migration path, and a `ZodError` if the ladder
 * produced an invalid state (a bug, not user data at fault).
 */
export function migrate(state: unknown, fromVersion: number): PersistedState {
  if (!isRecord(state)) {
    throw new PersistMigrationError(`expected a state object, got ${typeof state}`);
  }
  if (!Number.isInteger(fromVersion) || fromVersion < 0) {
    throw new PersistMigrationError(`invalid fromVersion ${fromVersion}`);
  }
  if (fromVersion > PERSIST_VERSION) {
    throw new PersistMigrationError(
      `state is from the future (v${fromVersion} > v${PERSIST_VERSION}) — refusing to guess`,
    );
  }

  let current = state;
  for (let v = fromVersion; v < PERSIST_VERSION; v++) {
    const step = MIGRATIONS[v];
    if (step === undefined) {
      throw new PersistMigrationError(`no migration from v${v}`);
    }
    current = step(current);
  }

  return persistedStateSchema.parse(current);
}

/**
 * Parse a raw persisted/backup JSON string: reads `v` (a missing `v` means
 * version 0), migrates to the current version, validates. This is the single
 * entry point for both localStorage rehydration and backup-file import.
 */
export function parsePersistedState(json: string): PersistedState {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new PersistMigrationError("persisted state is not valid JSON");
  }
  if (!isRecord(raw)) {
    throw new PersistMigrationError("persisted state is not an object");
  }
  const v = typeof raw.v === "number" ? raw.v : 0;
  return migrate(raw, v);
}

/* Compile-time parity between the zod schema and the interface, both directions. */
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type AssertTrue<T extends true> = T;
export type PersistedStateParity = AssertTrue<
  MutuallyAssignable<z.output<typeof persistedStateSchema>, PersistedState>
>;
